import { useState, useEffect } from 'react';
import { recipes } from './recipeData';
import crossIcon from './assets/definholycross.svg';

function formatVolume(grams, gramsPerUnit, unit) {
  if (unit === 'large eggs') {
    return `~${Math.round(grams / gramsPerUnit)} eggs`;
  }
  const amount = grams / gramsPerUnit;
  if (unit === 'cups' && amount < 0.25) {
    const tbsp = grams / 15;
    if (tbsp < 1) return `${(grams / 5).toFixed(1)} tsp`;
    return `${tbsp.toFixed(1)} tbsp`;
  }
  return `${amount.toFixed(1)} ${unit}`;
}

function formatGrams(grams) {
  return grams < 10 ? grams.toFixed(1) : String(Math.round(grams));
}

function formatPct(pct) {
  return pct < 1 ? pct.toFixed(2) : pct.toFixed(1);
}

function recordView(recipeId) {
  try {
    const views = JSON.parse(localStorage.getItem('recipeViews') || '{}');
    views[recipeId] = (views[recipeId] || 0) + 1;
    localStorage.setItem('recipeViews', JSON.stringify(views));
  } catch { /* localStorage unavailable */ }
}

function CalculatorRecipe({ recipe, onBack }) {
  const [inputWeight, setInputWeight] = useState(0);

  useEffect(() => {
    recordView(recipe.id);
  }, [recipe.id]);

  return (
    <div className="Recipe">
      <div className="Recipe-toolbar">
        <button className="Recipe-back" onClick={onBack}>&larr; All Recipes</button>
        <button className="Recipe-print" onClick={() => window.print()}>Print</button>
      </div>
      <div className="Recipe-print-header">
        <img src={crossIcon} className="Recipe-print-logo" alt="Define Holy" />
        <span className="Recipe-print-brand">Define Holy</span>
      </div>
      <h1 className="Recipe-title">{recipe.name}</h1>
      <span className="Recipe-category">{recipe.category}</span>

      <div className="Recipe-calc-input">
        <label htmlFor="pork-weight">{recipe.baseLabel || 'Pork Belly'} Weight (grams)</label>
        <input
          id="pork-weight"
          type="number"
          min="0"
          value={inputWeight || ''}
          placeholder="Enter weight in grams"
          onChange={e => setInputWeight(Math.max(0, Number(e.target.value)))}
        />
      </div>

      <table className="Recipe-table">
        <thead>
          <tr>
            <th>Ingredient</th>
            <th>Grams</th>
            <th>Rate</th>
          </tr>
        </thead>
        <tbody>
          <tr className="Recipe-base-row">
            <td>{recipe.baseLabel || 'Pork Belly'}</td>
            <td>{inputWeight > 0 ? `${inputWeight}g` : '—'}</td>
            <td>—</td>
          </tr>
          {recipe.calculations.map(calc => (
            <tr key={calc.name}>
              <td>{calc.name}</td>
              <td>{inputWeight > 0 ? `${(inputWeight * calc.rate).toFixed(1)}g` : '—'}</td>
              <td>{(calc.rate * 100).toFixed(2)}%</td>
            </tr>
          ))}
        </tbody>
      </table>

      {recipe.instructions.length > 0 && (
        <div className="Recipe-instructions">
          <h2 className="Recipe-instructions-title">Instructions</h2>
          <ol className="Recipe-steps">
            {recipe.instructions.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      )}
      <div className="Recipe-print-footer">
        <img src={crossIcon} className="Recipe-print-logo" alt="Define Holy" />
        <span className="Recipe-print-brand">DefineHoly.com</span>
      </div>
    </div>
  );
}

function getAllIngredients(recipe) {
  if (recipe.ingredientSections) {
    return recipe.ingredientSections.flatMap(s => s.ingredients);
  }
  return recipe.ingredients || [];
}

function StandardRecipe({ recipe, onBack }) {
  const allIngredients = getAllIngredients(recipe);

  const [recipeYield, setRecipeYield] = useState(recipe.baseYield);
  const [baseIngredient, setBaseIngredient] = useState(recipe.defaultBase);
  const [showVolume, setShowVolume] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    recordView(recipe.id);
  }, [recipe.id]);

  useEffect(() => {
    setRecipeYield(recipe.baseYield);
    setBaseIngredient(recipe.defaultBase);
    setShowVolume(false);
    setHeroIndex(0);
  }, [recipe]);

  const images = recipe.images || [];

  const scale = recipeYield / recipe.baseYield;
  const baseIng = allIngredients.find(i => i.name === baseIngredient);
  const baseGrams = baseIng ? baseIng.grams : 1;

  function formatWeight(ing, scaledGrams) {
    if (ing.rangeDisplay) {
      const lo = Math.round(parseFloat(ing.rangeDisplay) * scale);
      const hi = Math.round(parseFloat(ing.rangeDisplay.split('\u2013')[1]) * scale);
      return `${lo}\u2013${hi}g`;
    }
    const base = `${formatGrams(scaledGrams)}g`;
    if (ing.showEggCount) {
      return `${base} (~${Math.round(scaledGrams / 50)} eggs)`;
    }
    return base;
  }

  function renderIngredientRow(ing, key) {
    if (ing.asNeeded) {
      return (
        <tr key={key}>
          <td>{ing.name}</td>
          <td>as needed</td>
          <td>—</td>
        </tr>
      );
    }
    const scaledGrams = ing.grams * scale;
    const pct = ing.grams > 0 ? (ing.grams / baseGrams) * 100 : 0;
    return (
      <tr key={key} className={ing.name === baseIngredient ? 'Recipe-base-row' : ''}>
        <td>
          {ing.name}
          {ing.note && <span className="Recipe-ing-note"> ({ing.note})</span>}
        </td>
        <td>
          {showVolume
            ? formatVolume(scaledGrams, ing.gramsPerUnit, ing.volumeUnit)
            : formatWeight(ing, scaledGrams)
          }
        </td>
        <td>{pct > 0 ? `${formatPct(pct)}%` : '—'}</td>
      </tr>
    );
  }

  const hasSections = !!recipe.ingredientSections;
  const hasInstructionSections = !!recipe.instructionSections;

  // Deduplicate ingredients for the base dropdown
  const uniqueIngredients = allIngredients.filter((ing, i, arr) =>
    ing.grams > 0 && arr.findIndex(x => x.name === ing.name) === i
  );

  return (
    <div className="Recipe">
      <div className="Recipe-toolbar">
        <button className="Recipe-back" onClick={onBack}>&larr; All Recipes</button>
        <button className="Recipe-print" onClick={() => window.print()}>Print</button>
      </div>
      <div className="Recipe-print-header">
        <img src={crossIcon} className="Recipe-print-logo" alt="Define Holy" />
        <span className="Recipe-print-brand">Define Holy</span>
      </div>
      {images.length > 0 && (
        <div className="Recipe-photos">
          <img
            className="Recipe-hero"
            src={images[Math.min(heroIndex, images.length - 1)]}
            alt={recipe.name}
          />
          {images.length > 1 && (
            <div className="Recipe-thumbs">
              {images.map((src, i) => (
                <button
                  key={src}
                  className={`Recipe-thumb${i === heroIndex ? ' Recipe-thumb--active' : ''}`}
                  onClick={() => setHeroIndex(i)}
                  aria-label={`Photo ${i + 1} of ${images.length}`}
                >
                  <img src={src} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <h1 className="Recipe-title">{recipe.name}</h1>
      <span className="Recipe-category">{recipe.category}</span>
      {recipe.note && <p className="Recipe-note">{recipe.note}</p>}

      <div className="Recipe-controls">
        <div className="Recipe-control-group">
          <label htmlFor="yield">{recipe.yieldLabel || 'Yield'}</label>
          <input
            id="yield"
            type="number"
            min="1"
            value={recipeYield}
            onChange={e => setRecipeYield(Math.max(1, Number(e.target.value)))}
          />
        </div>

        <div className="Recipe-control-group">
          <label htmlFor="scale">Scale</label>
          <input
            id="scale"
            type="number"
            min="0.1"
            step="0.1"
            value={scale.toFixed(2)}
            onChange={e => {
              const s = Math.max(0.1, Number(e.target.value));
              setRecipeYield(Math.round(recipe.baseYield * s));
            }}
          />
        </div>

        <div className="Recipe-control-group">
          <label htmlFor="base">Baker's % Base</label>
          <select
            id="base"
            value={baseIngredient}
            onChange={e => setBaseIngredient(e.target.value)}
          >
            {uniqueIngredients.map(i => (
              <option key={i.name} value={i.name}>{i.name}</option>
            ))}
          </select>
        </div>

        <div className="Recipe-control-group">
          <label>Display</label>
          <button
            className="Recipe-toggle"
            onClick={() => setShowVolume(!showVolume)}
          >
            {showVolume ? 'Volume' : 'Weight (g)'}
          </button>
        </div>
      </div>

      {hasSections ? (
        recipe.ingredientSections.map(section => (
          <div key={section.title} className="Recipe-section">
            <h2 className="Recipe-section-title">{section.title}</h2>
            <table className="Recipe-table">
              <thead>
                <tr>
                  <th>Ingredient</th>
                  <th>{showVolume ? 'Volume' : 'Grams'}</th>
                  <th>Baker's %</th>
                </tr>
              </thead>
              <tbody>
                {section.ingredients.map((ing, i) =>
                  renderIngredientRow(ing, `${section.title}-${i}`)
                )}
              </tbody>
            </table>
          </div>
        ))
      ) : (
        <table className="Recipe-table">
          <thead>
            <tr>
              <th>Ingredient</th>
              <th>{showVolume ? 'Volume' : 'Grams'}</th>
              <th>Baker's %</th>
            </tr>
          </thead>
          <tbody>
            {recipe.ingredients.map((ing, i) => renderIngredientRow(ing, i))}
          </tbody>
        </table>
      )}

      {recipe.servingNote && (
        <p className="Recipe-serving-note">{recipe.servingNote}</p>
      )}

      {hasInstructionSections ? (
        recipe.instructionSections.map(section => (
          <div key={section.title} className="Recipe-instructions">
            <h2 className="Recipe-instructions-title">{section.title}</h2>
            <ol className="Recipe-steps">
              {section.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        ))
      ) : recipe.instructions && recipe.instructions.length > 0 ? (
        <div className="Recipe-instructions">
          <h2 className="Recipe-instructions-title">Instructions</h2>
          <ol className="Recipe-steps">
            {recipe.instructions.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      ) : null}

      <div className="Recipe-print-footer">
        <img src={crossIcon} className="Recipe-print-logo" alt="Define Holy" />
        <span className="Recipe-print-brand">DefineHoly.com</span>
      </div>
    </div>
  );
}

function RecipeDetail({ recipeId, onBack }) {
  const recipe = recipes.find(r => r.id === recipeId);

  if (recipe.type === 'calculator') {
    return <CalculatorRecipe recipe={recipe} onBack={onBack} />;
  }
  return <StandardRecipe recipe={recipe} onBack={onBack} />;
}

export default RecipeDetail;
